import React, { useState } from "react";
import styled from "styled-components";

import { Button, Tag } from "../../../../design-system";
import {
  GeneratedLandingInfo,
  generateLandingInfo,
} from "../../../../database/landingPage/generateLandingInfo";
import { Community } from "../../../../types/Community";
import {
  CustomLandingHtmlInfo,
  SubscriptionLandingInfo,
} from "../../../../types/Paywall";
import WebsitePanel from "./WebsitePanel";
import CustomDomainPanel from "./CustomDomainPanel";

type Props = {
  communityId?: string;
  isActive?: boolean;
  community?: Community;
  landingInfo?: SubscriptionLandingInfo;
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
`;

const Intro = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0;
  max-width: 720px;
`;

const Warn = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-warning-text, #9a6700);
`;

const Preview = styled.div`
  border: 1px solid var(--color-border);
  background: var(--color-surface-subtle);
  border-radius: var(--radius-md);
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const PreviewTitle = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const PreviewSubtitle = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const FeatureList = styled.ul`
  margin: var(--space-2) 0 0;
  padding-left: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;

const StatusPanel = styled.div`
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
`;

const StatusTitle = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--space-3);
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const MetaLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const MetaValue = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  overflow-wrap: anywhere;
`;

const PreviewCode = styled.pre`
  max-height: 180px;
  overflow: auto;
  margin: 0;
  padding: var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  font-family: var(--font-mono, monospace);
  font-size: var(--font-size-xs);
  line-height: 1.5;
  color: var(--color-text-secondary);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
`;

const PublicLink = styled.a`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-accent);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const formatBytes = (value?: string) => {
  const bytes = new Blob([value || ""]).size;
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const formatDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const buildLandingUrl = (communityId?: string, community?: Community) => {
  const slug = (community?.slug || communityId || "").trim();
  return slug ? `https://tribed.io/c/${encodeURIComponent(slug)}` : "";
};

const getCustomHtmlSnippet = (customHtml?: CustomLandingHtmlInfo) => {
  const html = customHtml?.html?.trim() || "";
  if (!html) return "";
  return html.length > 1000 ? `${html.slice(0, 1000)}...` : html;
};

const LandingForm = ({ communityId, isActive, community, landingInfo }: Props) => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratedLandingInfo | null>(null);
  const customHtml = landingInfo?._customHtml;
  const hasCustomHtml = customHtml?.enabled === true && !!customHtml.html?.trim();
  const landingUrl = buildLandingUrl(communityId, community);

  const onGenerate = async () => {
    if (!communityId) return;
    setGenerating(true);
    setError(null);
    try {
      const landingInfo = await generateLandingInfo({ communityId });
      setResult(landingInfo);
    } catch (e: any) {
      setError(e?.message || "Could not generate landing page");
    } finally {
      setGenerating(false);
    }
  };

  if (!isActive) return null;

  return (
    <Wrap>
      <Intro>
        Generate the marketing copy for this coach's standalone web landing page.
        The copy is written from the community details, the main author's bio,
        and the learning plans, then saved so the landing site and the app both
        read it. Set the public URL slug and app store links on the General tab.
      </Intro>

      <StatusPanel>
        <StatusHeader>
          <StatusTitle>Published landing status</StatusTitle>
          <Tag variant={hasCustomHtml ? "success" : "neutral"} size="sm" withDot>
            {hasCustomHtml ? "Custom HTML active" : "Template fallback"}
          </Tag>
        </StatusHeader>

        <LinkRow>
          {landingUrl ? (
            <PublicLink href={landingUrl} target="_blank" rel="noreferrer">
              Open public landing page
            </PublicLink>
          ) : (
            <MetaValue>No landing URL yet</MetaValue>
          )}
        </LinkRow>

        <MetaGrid>
          <MetaItem>
            <MetaLabel>Source</MetaLabel>
            <MetaValue>{customHtml?.generator || "Cloud Function / template"}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>Version</MetaLabel>
            <MetaValue>{customHtml?.version || "—"}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>Updated</MetaLabel>
            <MetaValue>
              {formatDate(customHtml?.updatedAt || customHtml?.generatedAt)}
            </MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>HTML / CSS size</MetaLabel>
            <MetaValue>
              {hasCustomHtml
                ? `${formatBytes(customHtml?.html)} / ${formatBytes(customHtml?.css)}`
                : "—"}
            </MetaValue>
          </MetaItem>
        </MetaGrid>

        {hasCustomHtml && (
          <>
            <MetaGrid>
              <MetaItem>
                <MetaLabel>Custom title</MetaLabel>
                <MetaValue>{customHtml?.title || "—"}</MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>Description</MetaLabel>
                <MetaValue>{customHtml?.description || "—"}</MetaValue>
              </MetaItem>
            </MetaGrid>
            <PreviewCode>{getCustomHtmlSnippet(customHtml)}</PreviewCode>
          </>
        )}
      </StatusPanel>

      <div>
        <Button
          variant="ai"
          onClick={onGenerate}
          disabled={generating || !communityId}
        >
          {generating ? "Generating..." : "Generate landing copy"}
        </Button>
      </div>

      <Warn>
        Regenerating overwrites the current landing copy, including any manual
        edits. The app keeps showing the previous copy until this finishes.
      </Warn>

      {error && <Warn>{error}</Warn>}

      {result && (
        <Preview>
          <PreviewTitle>{result.title}</PreviewTitle>
          <PreviewSubtitle>{result.subtitle}</PreviewSubtitle>
          {Array.isArray(result.features) && result.features.length > 0 && (
            <FeatureList>
              {result.features.slice(0, 8).map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </FeatureList>
          )}
        </Preview>
      )}

      <WebsitePanel communityId={communityId} community={community} />
      <CustomDomainPanel
        communityId={communityId}
        initialDomain={(landingInfo as any)?._customDomain || null}
      />
    </Wrap>
  );
};

export default LandingForm;
